export function orgJsonLd({
  name = "VilleSalento",
  url = "https://www.villesalento.com",
  logo,
  sameAs = [],
}: { name?: string; url?: string; logo?: string; sameAs?: string[] }) {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "TravelAgency", "RealEstateAgent"],
    name,
    url,
    description: "Piattaforma di prenotazione ville di lusso nel Salento. Scopri ville esclusive con piscina, vista mare e servizi premium in località come Gallipoli, Otranto e Santa Maria di Leuca.",
    ...(logo ? { logo } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      areaServed: "IT",
      availableLanguage: ["Italian", "English", "French", "German"],
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "IT",
      addressRegion: "Puglia",
      addressLocality: "Salento",
    },
    makesOffer: {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "Prenotazione ville di lusso",
        description: "Servizio di prenotazione ville di lusso nel Salento con servizi aggiuntivi personalizzabili",
      },
    },
  };
}

export function breadcrumbJsonLd(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function faqPageJsonLd(
  faqs: { question: string; answer: string }[],
  pageUrl?: string,
  aboutLocation?: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(pageUrl ? { url: pageUrl } : {}),
    ...(aboutLocation ? {
      about: {
        "@type": "Place",
        name: aboutLocation,
      }
    } : {}),
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
        ...(faq.answer.length > 100 ? { 
          inLanguage: "it",
        } : {}),
      },
    })),
  };
}

export function vacationRentalJsonLd({
  title,
  description,
  images = [],
  address,
  locality,
  region,
  country = "IT",
  pricePerNight,
  canonicalUrl,
  latitude,
  longitude,
  rating,
  reviewCount,
  bedrooms,
  bathrooms,
  maxGuests,
  amenities = [],
  checkInTime,
  checkOutTime,
}: {
  title: string;
  description?: string;
  images?: string[];
  address?: string;
  locality?: string;
  region?: string;
  country?: string;
  pricePerNight?: number;
  canonicalUrl: string;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number;
  reviewCount?: number;
  bedrooms?: number;
  bathrooms?: number;
  maxGuests?: number;
  amenities?: string[];
  checkInTime?: string;
  checkOutTime?: string;
}) {
  const json: any = {
    "@context": "https://schema.org",
    "@type": ["VacationRental", "Accommodation", "Product"],
    name: title,
    url: canonicalUrl,
  };
  
  if (description) {
    json.description = description;
  }
  
  if (images.length) {
    json.image = images;
    json.photo = images;
  }
  
  if (address || locality || region || country) {
    json.address = {
      "@type": "PostalAddress",
      ...(address ? { streetAddress: address } : {}),
      ...(locality ? { addressLocality: locality } : {}),
      ...(region ? { addressRegion: region } : {}),
      ...(country ? { addressCountry: country } : {}),
    };
  }
  
  if (latitude && longitude) {
    json.geo = {
      "@type": "GeoCoordinates",
      latitude: String(latitude),
      longitude: String(longitude),
    };
  }
  
  if (pricePerNight != null) {
    json.offers = {
      "@type": "Offer",
      price: String(pricePerNight),
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: canonicalUrl,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(pricePerNight),
        priceCurrency: "EUR",
        unitText: "notte",
      },
    };
  }
  
  if (rating && reviewCount) {
    json.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(rating.toFixed(1)),
      reviewCount: String(reviewCount),
      bestRating: "5",
      worstRating: "1",
    };
  }
  
  if (bedrooms) {
    json.numberOfRooms = bedrooms;
    json.numberOfBedrooms = bedrooms;
  }
  
  if (bathrooms) json.numberOfBathroomsTotal = bathrooms;
  if (maxGuests) json.occupancy = { "@type": "QuantitativeValue", value: maxGuests };
  
  if (amenities.length) {
    json.amenityFeature = amenities.map(amenity => ({
      "@type": "LocationFeatureSpecification",
      name: amenity,
      value: true,
    }));
  }
  
  if (checkInTime) json.checkinTime = checkInTime;
  if (checkOutTime) json.checkoutTime = checkOutTime;
  
  json.additionalType = "https://schema.org/LodgingBusiness";
  json.category = "Villa di lusso Salento";
  
  return json;
}

export function placeJsonLd({
  name,
  description,
  url,
  latitude,
  longitude,
  images = [],
  address,
  locality,
  region = "Puglia",
  country = "IT",
}: {
  name: string;
  description?: string;
  url: string;
  latitude?: number | null;
  longitude?: number | null;
  images?: string[];
  address?: string;
  locality?: string;
  region?: string;
  country?: string;
}) {
  const json: any = {
    "@context": "https://schema.org",
    "@type": ["Place", "TouristDestination"],
    name,
    url,
  };

  if (description) json.description = description;
  if (images.length) json.image = images;

  if (address || locality || region || country) {
    json.address = {
      "@type": "PostalAddress",
      ...(address ? { streetAddress: address } : {}),
      ...(locality ? { addressLocality: locality } : {}),
      ...(region ? { addressRegion: region } : {}),
      ...(country ? { addressCountry: country } : {}),
    };
  }

  if (latitude && longitude) {
    json.geo = {
      "@type": "GeoCoordinates",
      latitude: String(latitude),
      longitude: String(longitude),
    };
  }

  json.containedInPlace = {
    "@type": "Place",
    name: "Salento",
    address: {
      "@type": "PostalAddress",
      addressRegion: "Puglia",
      addressCountry: "IT",
    },
  };

  return json;
}

export function itemListJsonLd({
  items,
  listName,
  description,
}: {
  items: { name: string; url: string; position: number; image?: string }[];
  listName: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    ...(description ? { description } : {}),
    numberOfItems: items.length,
    itemListElement: items.map(item => ({
      "@type": "ListItem",
      position: item.position,
      name: item.name,
      url: item.url,
      ...(item.image ? { image: item.image } : {}),
    })),
  };
}

export function buildHrefLangAlternates(path: string, baseUrl: string = "https://www.villesalento.com") {
  const languages = ["it", "en", "fr", "de"];
  return languages.map(lang => ({
    hrefLang: lang,
    href: `${baseUrl}${path}?lang=${lang}`,
  }));
}
